import type { FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import { SelectContextProvider } from 'Components/smallComponents/select';
import type {
  RepeaterFunctionInvoker,
  TemplateContextMap,
  TemplateFunction,
  TemplateResult,
} from 'Components/types';
import { createFilteredFacetsSelector } from 'Core/selectors/search';
import { useSelector } from 'Core/store';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import { DisplayMode } from 'Modules/serverApi/types';
import isMobile from 'Vendor/isMobile';

import BaseFacet from './baseFacet';

export interface Params {
  facets: RepeaterFunctionInvoker<TemplateContextMap>;
  isMobile: boolean;
  hasSelection: boolean;
}

interface Props {
  template: TemplateFunction<Params>;
  fields?: string[];
  ignoreFields?: string[];
}

const Facets: FunctionComponent<Props> = ({ template, fields, ignoreFields }) => {
  const displayedFacets = useSelector(
    useMemo(() => {
      const { subFacetFields } = facetsConfig;
      const fullIgnoreFields = ignoreFields
        ? ignoreFields.concat(subFacetFields as string[])
        : subFacetFields;
      return createFilteredFacetsSelector(fields, fullIgnoreFields);
    }, [fields, ignoreFields]),
  );

  if (!displayedFacets.length) {
    return null;
  }

  const facets = displayedFacets.map((facet) => (templ: TemplateFunction<TemplateContextMap>) => {
    if (facet.displayMode === DisplayMode.None) {
      return null;
    }

    const props = { template: templ, facet };
    return (<BaseFacet {...props} key={facet.field} />) as TemplateResult;
  });

  return (
    <SelectContextProvider>
      {template.call({ facets, isMobile, hasSelection: displayedFacets.selection.length > 0 })}
    </SelectContextProvider>
  );
};

export default Facets;
